import { AdaptiveSelect } from '../AdaptiveSelect';
import type { AddressDetail, CompanyCardDto, PreparationState, Site } from '@bk/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useTenantLogo } from '../lib/brand';
import { Badge, Button, Card, ErrorText } from '../ui';
import { CompanyBrandEditor, SiteBrandEditor } from './SiteBrand';
import { AddressInput } from './tenant/AddressInput';

const USES: Array<{ id: NonNullable<Site['use']>; label: string }> = [
  { id: 'operativa', label: 'Si lavora qui' },
  { id: 'interna', label: 'Solo uffici o magazzino' },
  { id: 'domicilio', label: 'Solo un indirizzo' },
];

function SiteCard({ tenantId, s, activities, companyLogo }: { tenantId: string; s: Site; activities: CompanyCardDto['attivita']; companyLogo: string | null }) {
  const qc = useQueryClient();
  const [name, setName] = useState(s.name);
  const [removing, setRemoving] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'sites'] });
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Site>(`/tenants/${tenantId}/sites/${s.id}`, { method: 'PATCH', body }),
    onSuccess: refresh,
  });
  const activity = useMutation({
    mutationFn: ({ id, stato }: { id: string; stato: 'attiva' | 'esclusa' }) => api(`/tenants/${tenantId}/company/activities/${id}/sites/${s.id}`, { method: 'PATCH', body: { stato } }),
    onSuccess: refresh,
  });
  const busy = patch.isPending || activity.isPending;
  const here = new Set(s.attivita_in_sede.map((a) => a.id));
  const others = activities.filter((a) => !here.has(a.id) && a.stato !== 'cessata');
  const place = [s.address, s.municipality, s.province && `(${s.province})`].filter(Boolean).join(' ');
  return (
    <li className="space-y-3 rounded-lg border border-black/[0.08] p-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <label className="sr-only" htmlFor={`sede-${s.id}`}>
            Nome della sede
          </label>
          <input
            id={`sede-${s.id}`}
            className="w-full rounded-md border border-transparent bg-transparent px-1 text-base font-semibold text-ink hover:border-black/10 focus:border-black/20 focus:outline-none"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name.trim() !== s.name && patch.mutate({ name: name.trim() })}
          />
          <p className="px-1 text-xs text-ink/55">
            {place || 'Indirizzo da completare'}
            {s.region ? ` · ${s.region}` : ''}
            {s.visura ? ` · ${s.visura}` : ''}
          </p>
        </div>
        {s.legal && <Badge tone="blue">Sede legale</Badge>}
      </div>
      {!s.address && <AddressForm tenantId={tenantId} s={s} />}

      <fieldset className="flex flex-wrap gap-1.5">
        <legend className="mb-1 text-xs font-medium uppercase tracking-wide text-ink/45">Uso</legend>
        {USES.map((u) => (
          <button
            key={u.id}
            type="button"
            disabled={busy}
            aria-pressed={s.use === u.id}
            onClick={() => patch.mutate({ use: u.id })}
            className={`rounded-full border px-3 py-1 text-xs ${s.use === u.id ? 'border-ink bg-ink text-white' : 'border-black/15 text-ink/70 hover:border-black/30'}`}
          >
            {u.label}
          </button>
        ))}
      </fieldset>

      {s.use !== 'domicilio' && s.use !== 'interna' && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink/45">Attività in questa sede</p>
          {s.attivita_in_sede.length ? (
            <ul className="space-y-1">
              {s.attivita_in_sede.map((a) => (
                <li key={a.id}>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" disabled={busy} checked={a.stato !== 'esclusa'} onChange={(e) => activity.mutate({ id: a.id, stato: e.target.checked ? 'attiva' : 'esclusa' })} />
                    <span className={a.stato === 'esclusa' ? 'text-ink/40 line-through' : 'text-ink'}>
                      {a.name}
                      {a.ateco && <span className="ml-1 text-xs text-ink/45">ATECO {a.ateco}</span>}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink/55">Nessuna attività: aggiungine una se qui si lavora.</p>
          )}
          {others.length > 0 && (
            <AdaptiveSelect
              className="mt-2 rounded-md border border-black/15 bg-white px-2 py-1 text-xs text-ink"
              value=""
              disabled={busy}
              aria-label="Aggiungi un'attività dell'impresa in questa sede"
              onChange={(e) => e.target.value && activity.mutate({ id: e.target.value, stato: 'attiva' })}
            >
              <option value="">+ Aggiungi un’attività dell’impresa</option>
              {others.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                  {a.ateco ? ` (${a.ateco})` : ''}
                </option>
              ))}
            </AdaptiveSelect>
          )}
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink/45">Marchio</p>
        <SiteBrandEditor tenantId={tenantId} s={s} manager companyLogo={companyLogo} />
      </div>

      <div className="text-xs">
        {removing ? (
          <span className="flex flex-wrap items-center gap-2 text-ink/70">
            Togli la sede perché:
            <Button variant="ghost" disabled={busy} onClick={() => patch.mutate({ status: 'chiusa', motivo: 'non_esiste_piu' })}>
              Non c’è più
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => patch.mutate({ status: 'chiusa', motivo: 'non_nostra' })}>
              Non è nostra
            </Button>
            <button type="button" className="underline" onClick={() => setRemoving(false)}>
              Annulla
            </button>
          </span>
        ) : (
          <button type="button" className="text-ink/50 underline hover:text-ink" onClick={() => setRemoving(true)}>
            Non è più nostra
          </button>
        )}
      </div>
      <ErrorText>{patch.error || activity.error ? errorMessage(patch.error ?? activity.error) : ''}</ErrorText>
    </li>
  );
}

/** I pezzi di un indirizzo scelto tra i suggerimenti di Google (ADR 0028), senza quelli che non ha. */
function placeOf(a: AddressDetail): Record<string, string> {
  const { address, postal_code, municipality, province, region, country } = a;
  return Object.fromEntries(Object.entries({ address, postal_code, municipality, province, region, country }).flatMap(([k, v]) => (v ? [[k, v]] : [])));
}

/**
 * L'indirizzo di una sede: scelto tra i suggerimenti di Google mentre si scrive (ADR 0028), che danno già comune,
 * CAP e provincia giusti, senza scriverli a mano. Solo se il server non ha il servizio l'indirizzo si scrive in una riga.
 */
function PlaceInput({ tenantId, onPick }: { tenantId: string; onPick: (place: Record<string, string> | null) => void }) {
  const [picked, setPicked] = useState('');
  const [manual, setManual] = useState(false);
  if (manual)
    return (
      <input
        aria-label="Indirizzo della sede"
        className="w-full rounded-md border border-black/15 px-2 py-1 text-sm"
        placeholder="Indirizzo, CAP e comune"
        onChange={(e) => onPick(e.target.value.trim() ? { address: e.target.value.trim() } : null)}
      />
    );
  return (
    <AddressInput
      tenantId={tenantId}
      value={picked}
      onPick={(a) => {
        setPicked(a.completo);
        onPick(placeOf(a));
      }}
      onUnavailable={() => setManual(true)}
    />
  );
}

/**
 * L'indirizzo di una sede che la visura elenca senza luogo (ADR 0210 § 2): dopo un trasferimento della sede la visura
 * rimanda al registro della Camera di commercio di prima, con il REA; il titolare lo sceglie qui.
 */
function AddressForm({ tenantId, s }: { tenantId: string; s: Site }) {
  const qc = useQueryClient();
  const [place, setPlace] = useState<Record<string, string> | null>(null);
  const save = useMutation({
    mutationFn: () => api<Site>(`/tenants/${tenantId}/sites/${s.id}`, { method: 'PATCH', body: { ...place } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'sites'] }),
  });
  return (
    <form
      className="space-y-2 rounded-md bg-amber-50 p-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (place) save.mutate();
      }}
    >
      <p className="text-xs text-amber-900">
        La visura non scrive l’indirizzo di questa sede{s.registry_ref ? ` (${s.registry_ref})` : ''}: lo trovi nella visura della Camera di commercio della sua provincia, oppure scrivilo tu.
      </p>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <PlaceInput tenantId={tenantId} onPick={setPlace} />
        </div>
        <Button type="submit" disabled={save.isPending || !place}>
          Salva
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </form>
  );
}

/** Una sede che la visura non riporta (un magazzino, un ufficio): nome e indirizzo scelto tra i suggerimenti di Google. */
function AddSite({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [place, setPlace] = useState<Record<string, string> | null>(null);
  const close = () => {
    setOpen(false);
    setName('');
    setPlace(null);
  };
  const add = useMutation({
    mutationFn: () => api<Site>(`/tenants/${tenantId}/sites`, { method: 'POST', body: { name: name.trim(), ...place } }),
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ['tenant', tenantId, 'sites'] });
    },
  });
  if (!open)
    return (
      <button type="button" className="text-sm text-ink/60 underline hover:text-ink" onClick={() => setOpen(true)}>
        + Aggiungi una sede che la visura non riporta
      </button>
    );
  return (
    <form
      className="space-y-2 rounded-lg border border-dashed border-black/15 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && place) add.mutate();
      }}
    >
      <input
        id="nuova-sede-name"
        className="w-full rounded-md border border-black/15 px-2 py-1 text-sm"
        placeholder="Nome della sede"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <PlaceInput tenantId={tenantId} onPick={setPlace} />
      <div className="flex gap-2">
        <Button type="submit" disabled={add.isPending || !name.trim() || !place}>
          Aggiungi
        </Button>
        <Button type="button" variant="ghost" onClick={close}>
          Annulla
        </Button>
      </div>
      <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
    </form>
  );
}

/**
 * Le sedi dell'azienda nella Fase 0 (ADR 0210 § 2): già compilate dalla visura, con l'uso, le attività e il marchio di
 * ognuna. Il titolare conferma o corregge; solo dopo si compone il gestionale.
 */
export function SediConferma({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ['tenant', tenantId, 'sites'], queryFn: () => api<{ data: Site[] }>(`/tenants/${tenantId}/sites`) });
  const card = useQuery({ queryKey: ['tenant', tenantId, 'company'], queryFn: () => api<CompanyCardDto>(`/tenants/${tenantId}/company`) });
  const companyLogo = useTenantLogo(tenantId);
  const confirm = useMutation({
    mutationFn: () => api<PreparationState>(`/tenants/${tenantId}/onboarding/preparation/sedi`, { method: 'POST' }),
    onSuccess: (x) => qc.setQueryData(['tenant', tenantId, 'preparation'], x),
  });
  const list = (sites.data?.data ?? []).filter((s) => !s.tolta && s.status !== 'chiusa');
  return (
    <Card className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-ink">Le sedi dell’azienda</h2>
        <p className="mt-1 text-sm text-ink/60">
          Le abbiamo preparate dalla visura. Ogni sede avrà nel gestionale le sue attività, i suoi obblighi del comune e della regione e il suo marchio. Controlla e conferma.
        </p>
      </div>
      <div className="rounded-lg border border-black/[0.08] p-3">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink/45">Marchio dell’azienda</p>
        <CompanyBrandEditor tenantId={tenantId} manager />
      </div>
      {sites.isLoading ? (
        <p className="text-sm text-ink/55">Carico le sedi…</p>
      ) : (
        <ul className="space-y-3">
          {list.map((s) => (
            <SiteCard key={s.id} tenantId={tenantId} s={s} activities={card.data?.attivita ?? []} companyLogo={companyLogo} />
          ))}
        </ul>
      )}
      <AddSite tenantId={tenantId} />
      <div className="flex flex-wrap items-center gap-3 border-t border-black/[0.06] pt-3">
        <Button disabled={confirm.isPending || !list.length} onClick={() => confirm.mutate()}>
          {confirm.isPending ? 'Un momento…' : 'Confermo le sedi e creo il gestionale'}
        </Button>
        {!list.length && !sites.isLoading && <span className="text-xs text-ink/55">Serve almeno una sede.</span>}
      </div>
      <ErrorText>{confirm.error ? errorMessage(confirm.error) : sites.error ? errorMessage(sites.error) : ''}</ErrorText>
    </Card>
  );
}
